/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AddMealInsightFeedbackRequest } from '../models/AddMealInsightFeedbackRequest';
import type { CreateMealInsightRequest } from '../models/CreateMealInsightRequest';
import type { SuccessBoolean } from '../models/SuccessBoolean';
import type { SuccessCreateMealInsightResponse } from '../models/SuccessCreateMealInsightResponse';
import type { SuccessMealInsightStatsResponse } from '../models/SuccessMealInsightStatsResponse';
import type { SuccessPageMealInsightHistoryResponse } from '../models/SuccessPageMealInsightHistoryResponse';
import type { SuccessVoid } from '../models/SuccessVoid';
import type { UpdateMealInsightRequest } from '../models/UpdateMealInsightRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class MealInsightControllerService {

    /**
     * Creates a meal insight for a given file. The file must be owned by the authenticated user.
     * @param requestBody
     * @returns SuccessCreateMealInsightResponse OK
     * @throws ApiError
     */
    public static createMealInsight(
        requestBody: CreateMealInsightRequest,
    ): CancelablePromise<SuccessCreateMealInsightResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v1/mealinsight',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Adds feedback to a meal insight.
     * @param mealInsightId
     * @param requestBody
     * @returns SuccessVoid OK
     * @throws ApiError
     */
    public static addMealInsightFeedback(
        mealInsightId: string,
        requestBody: AddMealInsightFeedbackRequest,
    ): CancelablePromise<SuccessVoid> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v1/mealinsight/insights/{mealInsightId}/feedback',
            path: {
                'mealInsightId': mealInsightId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Deletes a meal insight.
     * @param mealInsightId
     * @returns SuccessVoid OK
     * @throws ApiError
     */
    public static deleteMealInsight(
        mealInsightId: string,
    ): CancelablePromise<SuccessVoid> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/v1/mealinsight/insights/{mealInsightId}',
            path: {
                'mealInsightId': mealInsightId,
            },
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Updates a meal insight.
     * @param mealInsightId
     * @param requestBody
     * @returns SuccessVoid OK
     * @throws ApiError
     */
    public static updateMealInsight(
        mealInsightId: string,
        requestBody: UpdateMealInsightRequest,
    ): CancelablePromise<SuccessVoid> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/v1/mealinsight/insights/{mealInsightId}',
            path: {
                'mealInsightId': mealInsightId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Get aggregated nutrition stats for the authenticated user
     * Returns aggregated nutrition stats for a given period. Only 'type=day' is currently supported. 'date' is interpreted in the user's timezone and defaults to today.
     * @param type
     * @param date
     * @returns SuccessMealInsightStatsResponse Aggregated nutrition stats for the requested period.
     * @throws ApiError
     */
    public static getMealInsightStats(
        type: 'day',
        date?: string,
    ): CancelablePromise<SuccessMealInsightStatsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/mealinsight/stats',
            query: {
                'type': type,
                'date': date,
            },
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Get the authenticated user's meal insight history
     * Returns the authenticated user's meal insights, most recent first (by meal time), with pagination. Filtering and sorting are driven by the meal time (takenAt), which defaults to the upload time when no EXIF datetime or override is available. Optionally filter by meal identification status, a closed date range (from/to, both inclusive, interpreted in the user's timezone), or a single day. The 'day' parameter cannot be combined with 'from'/'to'.
     * @param mealIdentificationFilter
     * @param from
     * @param to
     * @param day
     * @param page Zero-based page index (0..N)
     * @param size The size of the page to be returned
     * @param sort Sorting criteria in the format: property(,asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
     * @returns SuccessPageMealInsightHistoryResponse OK
     * @throws ApiError
     */
    public static getMealInsightHistory(
        mealIdentificationFilter?: 'IDENTIFIED_ONLY' | 'UNIDENTIFIED_ONLY' | 'BOTH',
        from?: string,
        to?: string,
        day?: string,
        page?: any,
        size?: any,
        sort?: any,
    ): CancelablePromise<SuccessPageMealInsightHistoryResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/mealinsight/insights',
            query: {
                'mealIdentificationFilter': mealIdentificationFilter,
                'from': from,
                'to': to,
                'day': day,
                'page': page,
                'size': size,
                'sort': sort,
            },
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Returns whether the authenticated user has ever used meal insights (including deleted ones).
     * @returns SuccessBoolean OK
     * @throws ApiError
     */
    public static hasAnyMealInsights(): CancelablePromise<SuccessBoolean> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/mealinsight/exists',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

}
